<template>
    <div>
        <component
            v-if="currentQuestion"
            :is="currentQuestion.type"
            :question="currentQuestion"
        ></component>
        <div class="progress-bar">
            <span class="progress-bar__progress" :style="`transform: translateX(${((100 / store.questionInfo.length) * store.questionAnswers.length) - 100}%)`"></span>
            <div class="progress-bar-holder">
                <div class="progress-bar__previous" @click="handlePreviousQuestion">
                    <i>
                        <IconArrowLeft />
                    </i>
                    Vorige
                </div>
                <div class="button progress-bar__next --no-underline" @click="handleNextQuestion">{{ nextButtonCaption }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import store from '../store/store';

import CheckboxQuestionComponent from './question-types/checkbox-question-component';
import InputfieldQuestionComponent from './question-types/inputfield-question-component';
import RadioQuestionComponent from './question-types/radio-question-component';
import RadioIconQuestionComponent from './question-types/radio-icon-question-component';
import SummaryQuestionComponent from './question-types/summary-question-component';

import IconArrowLeft from './icons/arrow-left';

export default {
    components: {
        CheckboxQuestionComponent,
        InputfieldQuestionComponent,
        RadioQuestionComponent,
        RadioIconQuestionComponent,
        SummaryQuestionComponent,
        IconArrowLeft,
    },

    data() {
        return {
            store,
            sideQuestionIndex: 0,
            currentQuestion: null,
            atLastQuestion: false,
            nextButtonCaption: 'Volgende',
        }
    },

    watch: {

        // Show a new question when a new route is being invoked.
        $route (to, from) {
            this.showQuestionBySlug(this.$router.currentRoute.params.id);
        },

        // Update button caption if at the last question.
        atLastQuestion (value) {
            if (value) {
                this.nextButtonCaption = 'Bekijk Resultaat';
                return;
            }
            this.nextButtonCaption = 'Volgende';
        },
    },

    /**
     * Init: Load tool data from local storage, show the question determined by the route, set listener for last-question event.
     * 
     * @returns {Void}
     */
    mounted() {

        // Load tool data from local storage.
        const storeData = JSON.parse(localStorage.getItem('toolInfo'));
        for (const [key, value] of Object.entries(storeData)) {
            this.store[key] = value;
        }

        // Show the question determined by the route.
        this.showQuestionBySlug(this.$router.currentRoute.params.id);

        // Update last question var on event.
        EventBus.$on('last-question-change', (value) => {
            console.log(value);
            this.atLastQuestion = value;
        });
    },

    methods: {

        /**
         * Go one step back into the past.
         * 
         * @returns {Void}
         */
        handlePreviousQuestion() {
            this.$router.go(-1);
        },

        /**
         * Go one step forward into the future.
         * 
         * @returns {Void}
         */
        handleNextQuestion() {

            // Check if question was filled out.
            if (!this.currentQuestionAnswered()) {
                return;
            }

            // Update localStorage now answer of question has been updated.
            localStorage.setItem('toolInfo', JSON.stringify(this.store));

            // Go to the next question if there is one.
            const slug = this.getNextQuestionSlug();
            if (slug) {
                this.$router.push({
                    path: `/tool/${ slug }`
                });
                return;
            }

            // Go to results page if there are no more questions.
            document.location.href = '/tool/results';
        },

        /**
         * Checks if the current question has been answered.
         * 
         * @returns {Boolean}
         */
        currentQuestionAnswered() {
            if (this.currentQuestion.isSideQuestion) {
                if (this.store.questionAnswers[this.store.currentQuestionIndex].sideQuestions.answers[this.store.currentSideQuestionIndex].length) {
                    return true;
                }
                return false;
            }
            if (this.store.questionAnswers[this.store.currentQuestionIndex].answers.length) {
                return true;
            }
            return false;
        },

        /**
         * Get the slug of the next question.
         * 
         * @returns {String|null} Slug of next question or null if there is no next question.
         */
        getNextQuestionSlug() {

            // Find next side question.
            if (this.store.questionInfo[this.store.currentQuestionIndex].showSideQuestions) {

                // Define index of next side question.
                let nextSideQuestionIndex = 0;
                if (this.store.currentSideQuestionIndex !== null) {
                    nextSideQuestionIndex = this.store.currentSideQuestionIndex +1;
                }

                // Handle checkbox side questions a bit differently.
                if (this.store.questionInfo[this.store.currentQuestionIndex].type === 'CheckboxQuestionComponent') {

                    // Set currentSideQuestion index to value from store, or -1 so it's lower than 0, and set nextSideQuestionIndex out of bounds in case there are no more questions.
                    const currentSideQuestionIndex = this.store.currentSideQuestionIndex !== null ? this.store.currentSideQuestionIndex : -1;
                    nextSideQuestionIndex = this.store.questionAnswers[this.store.currentQuestionIndex].answers.length;

                    // Loop answers to see which have been filled out and set the nextQuestionIndex to that index if it's higher than the current question index.
                    for (let i = 0; i < this.store.questionAnswers[this.store.currentQuestionIndex].answers.length; i++) {
                        if (this.store.questionAnswers[this.store.currentQuestionIndex].answers[i] && this.store.questionAnswers[this.store.currentQuestionIndex].answers[i].length > 0 && i > currentSideQuestionIndex) {
                            nextSideQuestionIndex = i;
                            break;
                        }
                    }
                }

                // Go there if there is a next side question.
                if (this.store.questionInfo[this.store.currentQuestionIndex].sideQuestions[nextSideQuestionIndex] !== undefined) {
                    return this.store.questionInfo[this.store.currentQuestionIndex].sideQuestions[nextSideQuestionIndex].slug;
                }
            }

            // Go to next main question if it exists.
            if (this.store.questionInfo[this.store.currentQuestionIndex +1] !== undefined) {
                return this.store.questionInfo[this.store.currentQuestionIndex +1].slug;
            }

            // End has been reached.
            return null;
        },

        /**
         * Show a question with the slug that was given.
         * 
         * @param {String} slug
         * @returns {Void}
         */
        showQuestionBySlug(slug) {

            // Find the right question and set the correct indexes.
            for (let i = 0; i < this.store.questionInfo.length; i++) {

                // Set question active and reset side questions index if this is a main question.
                if (this.store.questionInfo[i].slug == slug) {
                    this.currentQuestion = this.store.questionInfo[i];
                    this.store.currentSideQuestionIndex = null;
                    this.store.currentQuestionIndex = i;
                    break;
                }

                // Check for side questions and update indexes accordingly.
                if (this.store.questionInfo[i].showSideQuestions) {
                    for (let j = 0; j < this.store.questionInfo[i].sideQuestions.length; j++) {
                        if (this.store.questionInfo[i].sideQuestions[j].slug == slug) {
                            this.currentQuestion = this.store.questionInfo[i].sideQuestions[j];
                            this.store.currentSideQuestionIndex = j;
                            this.store.currentQuestionIndex = i;
                            break;
                        }
                    }
                }
            }

            // Go back to root if there is no question (that means the store is empty).
            if (!this.currentQuestion) {
                document.location.href = '/tool';
            }

            // Update localStorage with new question indexes.
            localStorage.setItem('toolInfo', JSON.stringify(this.store));

            // Set last question if there's no more after this one.
            const nextQuestionSlug = this.getNextQuestionSlug();
            this.atLastQuestion = false;
            if (slug != 'ruimtes' && nextQuestionSlug === null) {
                this.atLastQuestion = true;
            }
        },
    },
}
</script>
